import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const ADMIN_EMAIL = "admin@demo.ezyclass";
const TEACHER_EMAIL = "teacher@demo.ezyclass";
const STUDENT_EMAIL = "student@demo.ezyclass";

const TEACHER_NAMES = [
  "Nguyễn Thị Hương", "Trần Văn Minh", "Lê Thị Mai", "Phạm Đức Anh", "Hoàng Thị Lan",
  "Vũ Văn Hùng", "Đặng Thị Thu", "Bùi Quang Hải", "Đỗ Thị Ngọc", "Ngô Văn Tuấn",
  "Dương Thị Linh", "Lý Văn Phong", "Trịnh Thị Hà", "Mai Văn Đức", "Phan Thị Yến",
  "Hồ Văn Long", "Tô Thị Thanh", "Đinh Văn Khoa", "Lương Thị Bích", "Cao Văn Nam",
  "Nghiêm Thị Loan", "Thái Văn Sơn", "Châu Thị Mỹ", "Quách Văn Tâm", "Kiều Thị Hạnh",
  "Tạ Văn Bình", "La Thị Oanh", "Trương Văn Hiếu", "Đoàn Thị Vân", "Lưu Văn Trọng",
  "Võ Thị Kim", "Huỳnh Văn Tú", "Đàm Thị Quyên", "Uông Văn Đạt", "Mạc Thị Duyên",
  "Tăng Văn Vinh", "Khuất Thị Nhung", "Giáp Văn Lâm", "Từ Thị Phượng", "Cù Văn Toàn",
  "Bạch Thị Diệu", "Liêu Văn Hưng", "Sầm Thị Yến", "Phùng Văn Quốc", "Vy Thị Nga",
  "Triệu Văn Kiên", "Mã Thị Dung", "Hà Văn Cường", "Âu Thị Thảo", "Lã Văn Thắng",
];

const LAST_NAMES = ["Nguyễn", "Trần", "Lê", "Phạm", "Hoàng", "Vũ", "Đặng", "Bùi", "Đỗ", "Ngô", "Dương", "Lý"];
const MIDDLE_NAMES_M = ["Văn", "Minh", "Đức", "Quốc", "Bảo", "Hoàng", "Thanh", "Hữu"];
const FIRST_NAMES_M = ["An", "Bình", "Cường", "Đạt", "Hải", "Hùng", "Khoa", "Long", "Minh", "Nam", "Phong", "Quân", "Sơn", "Tâm", "Tuấn", "Vinh"];
const FIRST_NAMES_F = ["Anh", "Chi", "Diệu", "Hà", "Hạnh", "Lan", "Linh", "Mai", "Ngọc", "Oanh", "Phương", "Thảo", "Thu", "Trang", "Vy", "Yến"];

const SUBJECTS = ["Toán", "Tiếng Anh", "Vật lý", "Hóa học", "Ngữ văn", "Sinh học", "Lịch sử", "Tin học"];
const LEVELS = ["10", "11", "12", "Cơ bản", "Nâng cao"];
const SCHEDULES = ["T2,T5 18:00-19:30", "T3,T6 19:00-20:30", "T4,T7 8:00-9:30", "CN 9:00-11:00"];
const AVATARS = ["cat", "dog", "star", "heart", "rocket", "fire", "rainbow", "moon", "sun", "leaf"];

type Row = Record<string, unknown>;

async function batchInsert(table: string, rows: Row[], size = 500) {
  for (let i = 0; i < rows.length; i += size) {
    const chunk = rows.slice(i, i + size);
    const { error } = await supabaseAdmin.from(table as never).insert(chunk as never);
    if (error) {
      console.error(`[seed] insert ${table} chunk ${i}:`, error.message);
      throw new Error(`Seed ${table} failed: ${error.message}`);
    }
  }
}

async function batchUpsert(table: string, rows: Row[], onConflict: string, size = 500) {
  for (let i = 0; i < rows.length; i += size) {
    const chunk = rows.slice(i, i + size);
    const { error } = await supabaseAdmin
      .from(table as never)
      .upsert(chunk as never, { onConflict });
    if (error) {
      console.error(`[seed] upsert ${table} chunk ${i}:`, error.message);
      throw new Error(`Seed ${table} failed: ${error.message}`);
    }
  }
}

async function listDemoAuthUsersByEmail(emails: string[]) {
  const wanted = new Set(emails.map((email) => email.toLowerCase()));
  const found = new Map<string, string>();

  for (let page = 1; page <= 20 && found.size < wanted.size; page += 1) {
    const { data, error } = await supabaseAdmin.auth.admin.listUsers({ page, perPage: 1000 });
    if (error) throw new Error(`List auth users failed: ${error.message}`);
    const users = data.users ?? [];
    for (const user of users) {
      const email = user.email?.toLowerCase();
      if (email && wanted.has(email)) found.set(email, user.id);
    }
    if (users.length < 1000) break;
  }

  return found;
}

/**
 * Create missing auth users in parallel and reuse existing demo emails.
 * Returns auth user ids in input order.
 */
async function createAuthUsers(
  emails: string[],
  concurrency = 10,
): Promise<string[]> {
  const existingByEmail = await listDemoAuthUsersByEmail(emails);
  const ids: string[] = new Array(emails.length);
  let cursor = 0;
  async function worker() {
    while (true) {
      const i = cursor++;
      if (i >= emails.length) return;
      const email = emails[i];
      const existingId = existingByEmail.get(email.toLowerCase());
      if (existingId) {
        ids[i] = existingId;
        continue;
      }
      const { data, error } = await supabaseAdmin.auth.admin.createUser({
        email,
        password: "demo1234",
        email_confirm: true,
      });
      if (error || !data.user) {
        if (error?.message.toLowerCase().includes("already been registered")) {
          const refreshed = await listDemoAuthUsersByEmail([email]);
          const refreshedId = refreshed.get(email.toLowerCase());
          if (refreshedId) {
            ids[i] = refreshedId;
            continue;
          }
        }
        throw new Error(`Create auth user ${email} failed: ${error?.message}`);
      }
      ids[i] = data.user.id;
    }
  }
  await Promise.all(Array.from({ length: concurrency }, worker));
  return ids;
}

/**
 * Seed dữ liệu demo bắt mắt cho trung tâm Ezyclass:
 * - 50 giáo viên, 1000 học sinh, 30 lớp
 * - Lịch học 6 tháng (~24 tuần × 2 buổi/lớp)
 * - Điểm danh, bài tập, nộp bài, học phí, lương GV, thông báo
 *
 * Idempotent: xoá data demo cũ (gắn với 3 account demo) trước khi seed mới.
 * KHÔNG ảnh hưởng đến user thật.
 */
export const seedDemoData = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
  // Chỉ admin demo (admin@demo.ezyclass) mới được phép seed — chặn DoS qua
  // việc xoá + ghi lại ~10k+ rows bằng service-role.
  const callerEmail = (context.claims?.email as string | undefined)?.toLowerCase();
  if (callerEmail !== ADMIN_EMAIL) {
    throw new Error("Forbidden: chỉ admin demo mới có thể seed dữ liệu.");
  }
  // 1. Lấy 3 demo user IDs
  const { data: demoProfiles, error: profErr } = await supabaseAdmin
    .from("profiles")
    .select("id, email")
    .in("email", [ADMIN_EMAIL, TEACHER_EMAIL, STUDENT_EMAIL]);
  if (profErr) throw new Error(profErr.message);

  const idMap = new Map((demoProfiles ?? []).map((p) => [p.email, p.id]));
  const ADMIN_ID = idMap.get(ADMIN_EMAIL);
  const TEACHER_ID = idMap.get(TEACHER_EMAIL);
  const STUDENT_ID = idMap.get(STUDENT_EMAIL);
  if (!ADMIN_ID || !TEACHER_ID || !STUDENT_ID) {
    throw new Error("Demo accounts chưa tồn tại. Hãy thử đăng nhập demo một lần trước.");
  }

  // 1b. Ensure demo center exists and admin demo is linked to it.
  const { data: adminProfile } = await supabaseAdmin
    .from("profiles").select("center_id").eq("id", ADMIN_ID).maybeSingle();
  let DEMO_CENTER_ID: string | null = adminProfile?.center_id ?? null;
  if (!DEMO_CENTER_ID) {
    const { data: existing } = await supabaseAdmin
      .from("centers").select("id").eq("slug", "demo").maybeSingle();
    if (existing) {
      DEMO_CENTER_ID = existing.id;
    } else {
      const { data: created, error: cErr } = await supabaseAdmin
        .from("centers")
        .insert({ name: "Trung tâm Ezyclass Demo", slug: "demo", admin_id: ADMIN_ID } as never)
        .select("id").single();
      if (cErr || !created) throw new Error("Tạo center demo thất bại: " + cErr?.message);
      DEMO_CENTER_ID = created.id;
    }
    await supabaseAdmin.from("profiles").update({ center_id: DEMO_CENTER_ID } as never).eq("id", ADMIN_ID);
  }
  // Sync teacher/student demo center too
  await supabaseAdmin.from("profiles").update({ center_id: DEMO_CENTER_ID } as never).in("id", [TEACHER_ID, STUDENT_ID]);

  // 2. Cleanup data demo cũ
  const { data: oldGenProfiles } = await supabaseAdmin
    .from("profiles")
    .select("id, email")
    .or("email.like.gv%@demo.ezyclass,email.like.hs%@demo.ezyclass");
  const oldIds = (oldGenProfiles ?? []).map((p) => p.id);
  const oldTeacherIds = (oldGenProfiles ?? [])
    .filter((p) => p.email.startsWith("gv"))
    .map((p) => p.id);

  if (oldIds.length) {
    // Lấy classes do admin demo tạo (qua teacher demo hoặc seeded teachers)
    const { data: oldClasses } = await supabaseAdmin
      .from("classes")
      .select("id")
      .or(`teacher_id.in.(${[TEACHER_ID, ...oldTeacherIds].join(",")})`);
    const oldClassIds = (oldClasses ?? []).map((c) => c.id);

    if (oldClassIds.length) {
      const { data: oldAssignments } = await supabaseAdmin
        .from("assignments").select("id").in("class_id", oldClassIds);
      const oldAssIds = (oldAssignments ?? []).map((a) => a.id);
      if (oldAssIds.length) {
        await supabaseAdmin.from("submissions").delete().in("assignment_id", oldAssIds);
      }
      await supabaseAdmin.from("assignments").delete().in("class_id", oldClassIds);

      const { data: oldSessions } = await supabaseAdmin
        .from("class_sessions").select("id").in("class_id", oldClassIds);
      const oldSessIds = (oldSessions ?? []).map((s) => s.id);
      if (oldSessIds.length) {
        await supabaseAdmin.from("attendances").delete().in("session_id", oldSessIds);
      }
      await supabaseAdmin.from("class_sessions").delete().in("class_id", oldClassIds);
      await supabaseAdmin.from("enrollments").delete().in("class_id", oldClassIds);
      await supabaseAdmin.from("classes").delete().in("id", oldClassIds);
    }

    await supabaseAdmin.from("student_fees").delete().in("student_id", oldIds);
    await supabaseAdmin.from("teacher_salaries").delete().in("teacher_id", oldIds);
    await supabaseAdmin.from("user_roles").delete().in("user_id", oldIds);
    await supabaseAdmin.from("profiles").delete().in("id", oldIds);
  }
  // Xoá thông báo cũ do admin demo tạo
  await supabaseAdmin.from("announcements").delete().eq("created_by", ADMIN_ID);

  const now = new Date();

  // 3. 50 giáo viên — tạo auth users (trigger tự tạo profiles row), rồi UPDATE
  const teacherEmails = TEACHER_NAMES.map((_, i) => `gv${i + 1}@demo.ezyclass`);
  const teacherIds = await createAuthUsers(teacherEmails);
  const teacherProfiles = TEACHER_NAMES.map((name, i) => ({
    id: teacherIds[i],
    email: teacherEmails[i],
    display_name: name,
    onboarded: true,
    avatar_key: AVATARS[i % AVATARS.length],
    phone: `090${String(1000000 + i).slice(0, 7)}`,
    monthly_salary: [5000000, 6000000, 7000000, 8000000, 5500000][i % 5],
    center_id: DEMO_CENTER_ID,
  }));
  await batchUpsert("profiles", teacherProfiles, "id", 100);
  await batchUpsert(
    "user_roles",
    teacherProfiles.map((p) => ({ user_id: p.id, role: "teacher" })),
    "user_id,role",
    500,
  );

  // 4. 30 lớp
  const allTeacherIds = [TEACHER_ID, ...teacherProfiles.map((p) => p.id)];
  const classes = Array.from({ length: 30 }, (_, i) => ({
    id: crypto.randomUUID(),
    name: `${SUBJECTS[i % SUBJECTS.length]} ${LEVELS[i % LEVELS.length]} - ${String.fromCharCode(65 + (i % 4))}`,
    subject: SUBJECTS[i % SUBJECTS.length],
    teacher_id: allTeacherIds[i % allTeacherIds.length],
    capacity: [25, 30, 35, 20][i % 4],
    status: i < 26 ? "active" : "paused",
    schedule_note: SCHEDULES[i % SCHEDULES.length],
    center_id: DEMO_CENTER_ID,
  }));
  // Lớp đầu giao cho TEACHER demo để teacher demo có lớp
  classes[0].teacher_id = TEACHER_ID;
  classes[1].teacher_id = TEACHER_ID;
  classes[2].teacher_id = TEACHER_ID;
  await batchInsert("classes", classes, 100);

  // 5. 1000 học sinh — student demo nằm trong list
  const studentProfiles: Row[] = [];
  studentProfiles.push({
    id: STUDENT_ID,
    // không update email/display vì đã có
  });
  // Update profile existing student demo
  await supabaseAdmin.from("profiles").update({
    monthly_tuition: 800000,
    phone: "0981000000",
    onboarded: true,
    center_id: DEMO_CENTER_ID,
  } as never).eq("id", STUDENT_ID);

  const STUDENT_COUNT = 999;
  const studentEmails = Array.from({ length: STUDENT_COUNT }, (_, i) => `hs${i + 1}@demo.ezyclass`);
  const studentIds = await createAuthUsers(studentEmails, 20);
  const genStudents = Array.from({ length: STUDENT_COUNT }, (_, i) => {
    const isFemale = i % 2 === 1;
    const lastName = LAST_NAMES[i % LAST_NAMES.length];
    const middle = isFemale ? "Thị" : MIDDLE_NAMES_M[i % MIDDLE_NAMES_M.length];
    const first = isFemale
      ? FIRST_NAMES_F[i % FIRST_NAMES_F.length]
      : FIRST_NAMES_M[i % FIRST_NAMES_M.length];
    const yr = 2006 + (i % 4);
    const mo = String(1 + (i % 12)).padStart(2, "0");
    const day = String(1 + (i % 28)).padStart(2, "0");
    return {
      id: studentIds[i],
      email: studentEmails[i],
      display_name: `${lastName} ${middle} ${first}`,
      onboarded: true,
      avatar_key: AVATARS[i % AVATARS.length],
      phone: `098${String(1000000 + i).slice(0, 7)}`,
      monthly_tuition: [500000, 800000, 1000000, 600000, 750000][i % 5],
      date_of_birth: `${yr}-${mo}-${day}`,
      center_id: DEMO_CENTER_ID,
    };
  });
  await batchUpsert("profiles", genStudents, "id", 100);
  await batchUpsert(
    "user_roles",
    genStudents.map((p) => ({ user_id: p.id, role: "student" })),
    "user_id,role",
    500,
  );

  const allStudents = [
    { id: STUDENT_ID as string, monthly_tuition: 800000 },
    ...genStudents.map((s) => ({ id: s.id as string, monthly_tuition: s.monthly_tuition as number })),
  ];

  // 6. Ghi danh — mỗi HS vào 1 lớp chính + 1 lớp phụ (≈ 60-70 HS/lớp)
  const enrollSet = new Set<string>();
  const enrollments: Row[] = [];
  const addEnroll = (student_id: string, class_id: string) => {
    const key = `${student_id}:${class_id}`;
    if (enrollSet.has(key)) return;
    enrollSet.add(key);
    enrollments.push({ student_id, class_id, is_active: true });
  };
  allStudents.forEach((s, i) => {
    addEnroll(s.id, classes[i % classes.length].id);
    addEnroll(s.id, classes[(i + 7) % classes.length].id);
  });
  // STUDENT demo phải nằm trong lớp của TEACHER demo (class 0, 1)
  addEnroll(STUDENT_ID as string, classes[0].id);
  addEnroll(STUDENT_ID as string, classes[1].id);
  await batchInsert("enrollments", enrollments, 500);

  // 7. Buổi học — 12 tuần (6 quá khứ + 6 tương lai) × 2 buổi/tuần × 30 lớp ≈ 720 sessions
  const sessions: { id: string; class_id: string; starts_at: string; ends_at: string; room: string; topic: string | null }[] = [];
  classes.forEach((cls, ci) => {
    const startHour = [18, 19, 8, 9][ci % 4];
    for (let w = -6; w <= 6; w++) {
      for (const dayOffset of [0, 3]) {
        const startsAt = new Date(now);
        startsAt.setDate(startsAt.getDate() + w * 7 + dayOffset);
        startsAt.setHours(startHour, 0, 0, 0);
        const endsAt = new Date(startsAt);
        endsAt.setHours(startHour + 1, 30, 0, 0);
        sessions.push({
          id: crypto.randomUUID(),
          class_id: cls.id,
          starts_at: startsAt.toISOString(),
          ends_at: endsAt.toISOString(),
          room: `P.${301 + (ci % 10)}`,
          topic: ["Ôn tập chương 1", "Bài tập nâng cao", "Kiểm tra 15 phút", "Lý thuyết chương 2", "Thực hành"][Math.abs(w + dayOffset) % 5],
        });
      }
    }
  });
  await batchInsert("class_sessions", sessions, 500);

  // 8. Điểm danh — chỉ cho buổi đã qua, giới hạn 200 sessions để tránh quá tải
  const pastSessions = sessions.filter((s) => new Date(s.starts_at) < now).slice(0, 200);
  const enrollByClass = new Map<string, string[]>();
  enrollments.forEach((e) => {
    const cid = e.class_id as string;
    if (!enrollByClass.has(cid)) enrollByClass.set(cid, []);
    enrollByClass.get(cid)!.push(e.student_id as string);
  });
  const attendances: Row[] = [];
  for (const s of pastSessions) {
    const students = (enrollByClass.get(s.class_id) ?? []).slice(0, 40);
    const teacher = classes.find((c) => c.id === s.class_id)?.teacher_id ?? ADMIN_ID;
    for (const sid of students) {
      const r = Math.random();
      attendances.push({
        session_id: s.id,
        student_id: sid,
        status: r < 0.75 ? "present" : r < 0.85 ? "late" : r < 0.95 ? "absent" : "excused",
        marked_by: teacher,
      });
    }
  }
  await batchInsert("attendances", attendances, 500);

  // 9. Bài tập — 4 bài/lớp = 120
  const assignments: Row[] = [];
  const submissions: Row[] = [];
  classes.forEach((cls) => {
    const clsSessions = sessions.filter((s) => s.class_id === cls.id).slice(0, 4);
    for (let a = 0; a < 4; a++) {
      const assignId = crypto.randomUUID();
      const due = new Date(now);
      due.setDate(due.getDate() + (a - 2) * 7);
      assignments.push({
        id: assignId,
        class_id: cls.id,
        title: ["Bài tập chương 1", "Kiểm tra 15 phút", "Bài tập nâng cao", "Ôn tập tổng hợp"][a],
        description: "Hoàn thành bài tập theo yêu cầu trong sách giáo khoa.",
        max_score: 10,
        due_at: due.toISOString(),
        created_by: cls.teacher_id,
        session_id: clsSessions[a]?.id ?? null,
      });
      const students = (enrollByClass.get(cls.id) ?? []).slice(0, 40);
      for (const sid of students) {
        if (Math.random() < 0.7) {
          const score = Math.random() < 0.8 ? Math.round(5 + Math.random() * 5) : null;
          submissions.push({
            assignment_id: assignId,
            student_id: sid,
            content: "Bài làm của học sinh.",
            submitted_at: new Date(due.getTime() - Math.random() * 86400000 * 3).toISOString(),
            score,
            feedback: score ? ["Tốt!", "Khá, cần cải thiện phần 2", "Xuất sắc!", "Cần ôn lại lý thuyết"][Math.floor(Math.random() * 4)] : null,
            graded_by: score ? cls.teacher_id : null,
            graded_at: score ? new Date().toISOString() : null,
          });
        }
      }
    }
  });
  await batchInsert("assignments", assignments, 200);
  await batchInsert("submissions", submissions, 500);

  // 10. Học phí 6 tháng
  const feeMonths = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now);
    d.setMonth(d.getMonth() - i);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  const fees: Row[] = [];
  for (const s of allStudents) {
    for (const month of feeMonths) {
      const tuition = s.monthly_tuition ?? 500000;
      const r = Math.random();
      const paid = r < 0.8 ? tuition : r < 0.9 ? Math.round(tuition * 0.5) : 0;
      fees.push({
        student_id: s.id,
        month,
        total_fee: tuition,
        paid_amount: paid,
        is_paid: paid >= tuition,
        paid_at: paid >= tuition ? new Date().toISOString() : null,
        created_by: ADMIN_ID,
        center_id: DEMO_CENTER_ID,
      });
    }
  }
  await batchUpsert("student_fees", fees, "student_id,month", 500);

  // 11. Lương GV 6 tháng
  const salaries: Row[] = [];
  for (const t of teacherProfiles) {
    for (const month of feeMonths) {
      const isPaid = Math.random() < 0.85;
      salaries.push({
        teacher_id: t.id,
        month,
        salary_amount: t.monthly_salary,
        is_paid: isPaid,
        paid_at: isPaid ? new Date().toISOString() : null,
        created_by: ADMIN_ID,
        center_id: DEMO_CENTER_ID,
      });
    }
  }
  await batchUpsert("teacher_salaries", salaries, "teacher_id,month", 500);

  // 12. Thông báo mẫu
  const announcements = [
    { title: "Lịch nghỉ Tết Nguyên đán 2026", body: "Trung tâm nghỉ Tết từ 25/01 đến 05/02. Chúc mừng năm mới!", audience: "all" },
    { title: "Cập nhật lịch học tháng 3", body: "Lịch học tháng 3 đã được cập nhật. Vui lòng kiểm tra lịch cá nhân.", audience: "all" },
    { title: "Nhắc nhở đóng học phí", body: "Học phí tháng này đã đến hạn. Vui lòng thanh toán trước ngày 15.", audience: "students" },
    { title: "Họp giáo viên định kỳ", body: "Cuộc họp định kỳ sẽ diễn ra lúc 14:00 ngày 20 tại phòng họp.", audience: "teachers" },
    { title: "Kết quả thi cuối kỳ", body: "Điểm thi cuối kỳ đã được cập nhật. Kiểm tra trong mục Bài tập.", audience: "students" },
    { title: "Chào mừng năm học mới!", body: "Chúc các em một năm học mới thật nhiều niềm vui và thành công!", audience: "all" },
    { title: "Lịch kiểm tra giữa kỳ", body: "Kiểm tra giữa kỳ sẽ diễn ra từ 10–15 tháng tới. Các em ôn tập kỹ nhé!", audience: "students" },
    { title: "Chương trình ôn thi đại học", body: "Lớp ôn thi đại học sẽ bắt đầu từ tháng 6. Đăng ký tại quầy lễ tân.", audience: "all" },
  ];
  await batchInsert(
    "announcements",
    announcements.map((a, i) => ({
      ...a,
      created_by: ADMIN_ID,
      center_id_owner: DEMO_CENTER_ID,
      created_at: new Date(now.getTime() - i * 86400000 * 3).toISOString(),
    })),
    100,
  );

  // 13. Cập nhật profile admin demo
  await supabaseAdmin.from("profiles").update({
    display_name: "Trung tâm Ezyclass Demo",
    extra_info: {
      centerName: "Trung tâm Giáo dục Ezyclass",
      centerAddress: "123 Nguyễn Huệ, Quận 1, TP. Hồ Chí Minh",
    },
  }).eq("id", ADMIN_ID);

  return {
    ok: true,
    stats: {
      teachers: teacherProfiles.length,
      students: allStudents.length,
      classes: classes.length,
      sessions: sessions.length,
      attendances: attendances.length,
      assignments: assignments.length,
      submissions: submissions.length,
      fees: fees.length,
      salaries: salaries.length,
    },
  };
});
